/**
 * SectorAllocationCard.jsx — Pie chart showing portfolio sector allocation
 * with click-to-drill-down into SectorDetailsModal.
 *
 * Uses Recharts PieChart + Tooltip.  Colour-coded by sector.
 * All sectors are shown individually (no "Others" grouping).
 */

import React, { useState, useCallback } from 'react';
import {
    PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Sector,
} from 'recharts';

/* ── helpers ── */
const fmt = (n, d = 2) => {
    if (n == null || isNaN(n)) return 'N/A';
    return Number(n).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d });
};
const fmtCurrency = (n) => (n == null || isNaN(n) ? 'N/A' : `₹${fmt(n)}`);
const fmtPct = (n) => (n == null || isNaN(n) ? 'N/A' : `${fmt(n)}%`);

const RADIAN = Math.PI / 180;

/* ── Outer label for each pie slice ── */
const renderCustomLabel = ({
    cx, cy, midAngle, innerRadius, outerRadius, name, percent, index,
}) => {
    // Only show labels for sectors > 2% to avoid overlap on tiny slices
    if (percent < 0.02) return null;
    const radius = outerRadius + 25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
        <text
            x={x}
            y={y}
            fill="var(--text-2)"
            textAnchor={x > cx ? 'start' : 'end'}
            dominantBaseline="central"
            fontSize={11}
            fontWeight={500}
        >
            {name} ({(percent * 100).toFixed(1)}%)
        </text>
    );
};

/* ── Active-sector shape (enlarged slice on hover) ── */
const renderActiveShape = (props) => {
    const {
        cx, cy, innerRadius, outerRadius, startAngle, endAngle,
        fill, payload, percent,
    } = props;
    return (
        <g>
            <text x={cx} y={cy - 8} textAnchor="middle" fill="var(--text-1)" fontSize={14} fontWeight={600}>
                {payload.name}
            </text>
            <text x={cx} y={cy + 14} textAnchor="middle" fill="var(--text-2)" fontSize={12}>
                {(percent * 100).toFixed(1)}%
            </text>
            <Sector
                cx={cx} cy={cy}
                innerRadius={innerRadius}
                outerRadius={outerRadius + 8}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
            />
            <Sector
                cx={cx} cy={cy}
                innerRadius={outerRadius + 12}
                outerRadius={outerRadius + 16}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
            />
        </g>
    );
};

/* ── Custom tooltip ── */
const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div className="sector-tooltip">
            <div className="sector-tooltip-header">
                <span className="sector-tooltip-dot" style={{ background: d.color }} />
                <strong>{d.name}</strong>
            </div>
            <div className="sector-tooltip-row">
                <span>Value</span>
                <span>{fmtCurrency(d.value)}</span>
            </div>
            <div className="sector-tooltip-row">
                <span>Weight</span>
                <span>{fmtPct(d.percentage)}</span>
            </div>
            <div className="sector-tooltip-row">
                <span>Holdings</span>
                <span>{d.holdingsCount}</span>
            </div>
        </div>
    );
};

/* ── Legend renderer ── */
const renderLegend = ({ payload, onLegendClick }) => (
    <ul className="sector-legend">
        {payload.map((entry, i) => (
            <li
                key={entry.value}
                className="sector-legend-item"
                onClick={() => onLegendClick && onLegendClick(entry.payload)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onLegendClick && onLegendClick(entry.payload)}
            >
                <span className="sector-legend-dot" style={{ background: entry.color }} />
                <span className="sector-legend-label">{entry.value}</span>
                <span className="sector-legend-pct">{fmtPct(entry.payload.percentage)}</span>
            </li>
        ))}
    </ul>
);

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */
const SectorAllocationCard = ({ sectorData = [], onSectorClick }) => {
    const [activeIndex, setActiveIndex] = useState(-1);

    const onPieEnter = useCallback((_, index) => setActiveIndex(index), []);
    const onPieLeave = useCallback(() => setActiveIndex(-1), []);

    const handleClick = useCallback((data) => {
        if (onSectorClick) onSectorClick(data);
    }, [onSectorClick]);

    if (!sectorData.length) {
        return (
            <section className="portfolio-section">
                <h3 className="portfolio-section-title">Sector Allocation</h3>
                <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
                    No sector data available. Sync your portfolio to see sector allocation.
                </p>
            </section>
        );
    }

    const totalValue = sectorData.reduce((s, d) => s + (d.value || 0), 0);

    return (
        <section className="portfolio-section sector-allocation-card">
            <h3 className="portfolio-section-title">Sector Allocation</h3>
            <p className="sector-subtitle">
                Click a sector to compare with its benchmark index
            </p>

            <div className="sector-chart-container">
                {/* Pie chart */}
                <div className="sector-pie-wrap">
                    <ResponsiveContainer width="100%" height={420}>
                        <PieChart>
                            <Pie
                                data={sectorData}
                                cx="50%"
                                cy="50%"
                                innerRadius={55}
                                outerRadius={100}
                                dataKey="value"
                                nameKey="name"
                                minAngle={3}
                                label={renderCustomLabel}
                                labelLine={{ stroke: 'var(--text-3)', strokeWidth: 1 }}
                                activeIndex={activeIndex}
                                activeShape={renderActiveShape}
                                onMouseEnter={onPieEnter}
                                onMouseLeave={onPieLeave}
                                onClick={(_, index) => handleClick(sectorData[index])}
                                style={{ cursor: 'pointer' }}
                                animationDuration={600}
                            >
                                {sectorData.map((entry, i) => (
                                    <Cell key={entry.name} fill={entry.color} stroke="var(--bg-card)" strokeWidth={2} />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                    </ResponsiveContainer>
                    {/* Center label */}
                    <div className="sector-center-label">
                        <div className="sector-center-value">{fmtCurrency(totalValue)}</div>
                        <div className="sector-center-sub">Total Value</div>
                    </div>
                </div>

                {/* Legend (right side) */}
                <div className="sector-legend-wrap">
                    {renderLegend({
                        payload: sectorData.map((s) => ({
                            value: s.name,
                            color: s.color,
                            payload: s,
                        })),
                        onLegendClick: handleClick,
                    })}
                </div>
            </div>
        </section>
    );
};

export default SectorAllocationCard;
